import { Match } from '@apis/index'
import { edit, outlineuser } from '@assets/icons'
import Column from '@components/column'
import Status from '@components/status'
import Table from '@components/table'
import { useModal } from '@context/ModalContext'
import styled from '@emotion/styled'
import { ProvideRoomDetails } from '@pages/admin/game-management/match-rooms/admin-created/success-alert-modal'
import IndividualUser from '@pages/admin/user-management/individual-user'
import { RootState } from '@store/index'
import theme from '@utils/theme'
import dayjs from 'dayjs'
import { CirclePlus } from 'lucide-react'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'Not Started', label: 'Not Started' },
  { value: 'On Going', label: 'On Going' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
]

const AdminCreatedMatches = ({
  isResult,
  userId,
}: {
  isResult?: boolean
  userId?: string
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const navigate = useNavigate()
  const { showModal } = useModal()
  const [editableInputs, setEditableInputs] = useState<Record<string, any>>({})
  const [page, setPage] = useState(1)
  const [id, setId] = useState<string | null>(null)
  // const [limit, setLimit] = useState<number | null>()

  const [expandedData, setExpandedData] = useState<Record<string, any[]>>({})
  const [joinedByPage, setJoinedByPage] = useState(1)
  const [joinedByUserResults, setJoinedByUserResults] = useState<any>()
  const [expandedLoading, setExpandedLoading] = useState(false)
  const {
    items: matches,
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => state.matches)
  const fetchJoinedByUsers = async (matchId?: string, limit?: number) => {
    setExpandedLoading(true)
    try {
      const result = await Match.joinedBy({
        params: { matchId, page: joinedByPage, limit },
      })
      console.log(result, 'match joined users')
      const initialInputs = {}
      result.items?.forEach((item) => {
        const id = item?.user?.id || item?.userId
        initialInputs[id] = {
          matchJoinId: item.id,
          userId: id,
          rank: item?.infos?.rank || '',
          noOfKills: item?.infos?.noOfKills || '',
          points: item?.points || '',
        }
      })

      setEditableInputs((prev) => ({ ...prev, ...initialInputs }))
      setExpandedData((prev) => ({ ...prev, [matchId]: result.items || [] }))
      setJoinedByUserResults(result)
    } catch (err) {
      console.error(err)
    } finally {
      setExpandedLoading(false)
    }
  }

  const fetchMatches = async (search = '', page = 1) => {
    try {
      await Match.list({
        state: 'matches',
        params: {
          page,
          limit: 30,
          isCreatedByAdmin: true,
          ...(userId && { userId }),
          ...(statusFilter && { status: statusFilter }),
          ...(search && { search }),
        },
      })
    } catch (error) {
      console.error('Failed to fetch admin-created matches:', error)
    }
  }
  useEffect(() => {
    setPage(1)
  }, [searchTerm])
  useEffect(() => {
    fetchMatches(searchTerm, page)
  }, [searchTerm, page, statusFilter])

  const columns = [
    { title: 'Match Id', key: 'matchId' },
    { title: 'Matchroom Name', key: 'matchroomName' },
    { title: 'Game Name', key: 'gameName' },
    { title: 'No. of Players', key: 'numberOfPlayers' },
    { title: 'Team Type', key: 'teamType' },
    { title: 'Entry Fee', key: 'entryFee' },
    {
      title: 'Game Time',
      key: 'gameTime',
      render: (item) => (
        <Column stylecolumn="gap:3px">
          <p>{item.date}</p>
          <p style={{ color: 'gray', fontSize: '12px' }}>
            {dayjs(`1970-01-01T${item.gameTime}`).format('hh:mm A')}
          </p>
        </Column>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (item) => (
        <Status
          item={item}
          statuses={['On Going', 'Completed', 'Cancelled']}
          onStatusChange={async (status) => {
            if (status === item.status) return
            try {
              await Match.updateStatus({
                id: item.id,
                state: 'matches',
                data: { status },
              })
            } catch (e) {
              toast.error('Failed to update status')
            }
          }}
        />
      ),
    },
  ]

  const data = matches.map((match) => ({
    id: match?.id ?? 'N/A',
    matchId: match?.id ?? 'N/A',
    matchroomName: match?.name ?? 'Unnamed',
    gameName: match?.type ?? 'Unknown',
    numberOfPlayers: match?.teamSize ?? 0,
    teamType: match?.infos?.gameSettings?.matchType ?? 'N/A',
    entryFee: match?.entryFee ?? 0,
    gameTime: match?.slot ?? 'N/A',
    date: match.date ?? 'N/A',
    status: match?.status ?? 'Not Started',
    hasSaveButton: isResult,
    infos: match?.infos ?? {},
    potentialWinning:
      match?.potentialWinning ?? match?.infos?.potentialWinning ?? 0,
  }))
  const buildChildColumns = () => [
    ...(isResult
      ? [
          {
            title: 'Room Position',
            key: 'roomPosition',
            render: (item, i) => <p>{i}</p>,
          },
        ]
      : []),
    {
      title: 'Player Name',
      key: 'player_name',
      render: (item) => {
        return (
          <p
            onClick={() => {
              showModal(<IndividualUser userStateId={item.id} />, {
                width: 1200,
              })
            }}
          >
            {item?.player_name}
          </p>
        )
      },
    },
    { title: 'Phone No.', key: 'phone_no' },
    {
      title: 'PUBG Tag',
      key: 'pubg_tag',
      render: (item) => (
        <span>
          {item.gamer_tag?.pubgGamerTag ? (
            item.gamer_tag.pubgGamerTag
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>
              No PUBG Tag
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Free Fire Tag',
      key: 'freefire_tag',
      render: (item) => (
        <span>
          {item.gamer_tag?.freeFireGamerTag ? (
            item.gamer_tag.freeFireGamerTag
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>
              No Free Fire Tag
            </span>
          )}
        </span>
      ),
    },
    ...(isResult
      ? [
          {
            title: 'Rank',
            key: 'rank',
            render: (item) => (
              <Input
                type="text"
                value={editableInputs[item.id]?.rank || ''}
                onChange={(e) =>
                  setEditableInputs((prev) => ({
                    ...prev,
                    [item.id]: {
                      ...prev[item.id],
                      matchJoinId: item.matchJoinId,
                      userId: item.id,
                      rank: e.target.value,
                    },
                  }))
                }
              />
            ),
          },
          {
            title: 'No of Kills',
            key: 'noOfKills',
            render: (item) => (
              <Input
                type="text"
                value={editableInputs[item.id]?.noOfKills || ''}
                onChange={(e) =>
                  setEditableInputs((prev) => ({
                    ...prev,
                    [item.id]: {
                      ...prev[item.id],
                      matchJoinId: item.matchJoinId,
                      userId: item.id,
                      noOfKills: e.target.value,
                    },
                  }))
                }
              />
            ),
          },
        ]
      : [
          {
            title: 'Claimed Status',
            key: 'claimedStatus',
            render: (item) => <Status item={item} />,
          },
        ]),
    ...(isResult
      ? [
          {
            title: 'Points',
            key: 'points',
            render: (item) => {
              const status =
                editableInputs[item.id]?.claimedStatus || item.claimedStatus
              const isDisabled = ['Win', 'Lose', 'Refund'].includes(status)
              return (
                <Input
                  style={{
                    border: '1px solid #49c55e',
                    color: '#49c55e',
                    opacity: isDisabled ? 0.7 : 1,
                  }}
                  type="text"
                  value={editableInputs[item.id]?.points || ''}
                  disabled={isDisabled}
                  onChange={(e) => {
                    let val = e.target.value
                    setEditableInputs((prev) => ({
                      ...prev,
                      [item.id]: {
                        ...prev[item.id],
                        matchJoinId: item.matchJoinId,
                        userId: item.id,
                        points: val,
                      },
                    }))
                  }}
                />
              )
            },
          },
        ]
      : []),
  ]

  const handleSave = async () => {
    const selectedMatch = matches.find((m) => m.id === id)
    if (!selectedMatch) {
      toast.error('No match selected')
      return
    }

    if (
      ['Completed', 'Cancelled', 'Not Started'].includes(selectedMatch.status)
    ) {
      toast.warning(`You cannot update a match that is ${selectedMatch.status}`)
      return
    }

    const updated = Object.entries(editableInputs)
      .filter(([_, val]) => val.rank || val.noOfKills || val.points)
      .map(([id, val]) => ({
        id: val.matchJoinId ?? '',
        userId: val.userId ?? id,
        points: Number(val.points) ?? 0,
        rank: Number(val.rank) ?? 0,
        noOfKills: Number(val.noOfKills) ?? 0,
      }))

    if (updated.length === 0) {
      toast.warning('No user data has been entered.')
      return
    }

    await toast.promise(
      Match.updateGameResultAdmin({
        id,
        data: { players: updated },
      }).then(async () => {
        await fetchJoinedByUsers(id)
      }),
      {
        pending: 'Saving match results...',
        success: 'Match results saved successfully!',
        error: 'Failed to save match results.',
      },
    )
    window.location.reload()
  }
  return (
    <Fragment>
      <FilterContainer>
        <StatusDropdown
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </StatusDropdown>
      </FilterContainer>
      <div style={{ transform: 'translateY(-60px)' }}>
        <Table
          onSearchChange={(val) => {
            setSearchTerm(val)
          }}
          loading={loading}
          onPageChange={setPage}
          totalItems={totalItems}
          totalPages={totalPages}
          pageControlled={page}
          itemsPerPage={30}
          data={data}
          columns={columns}
          text="Admin Created Matchrooms"
          icon={outlineuser}
          searchKeys={['name', 'matchId']}
          setLimit={(val) => {}}
          setId={(matchId, limit) => {
            setId(matchId)
            fetchJoinedByUsers(matchId, limit)
          }}
          onSave={handleSave}
          {...(!isResult && {
            actions: [
              {
                icon: <CirclePlus color={theme.colors.primary} />,
                key: 'add',
                onClick: (item) => {
                  const data = matches.find((m) => m.id === item.id)
                  showModal(<ProvideRoomDetails data={item} />)
                  // if (data.status !== 'Not Started') {
                  //   toast.warn(
                  //     'You can only add details to those matches that have not started yet',
                  //   )
                  // }
                },
              },
              {
                icon: edit,
                key: 'edit',
                onClick: (item) => {
                  const data = matches.find((m) => m.id === item.id)
                  if (data.status === 'Not Started') {
                    navigate(
                      `/admin/game-management/match-rooms/create-matchroom/${item.id}`,
                      {
                        state: data,
                      },
                    )
                  } else {
                    toast.warn(
                      'You can only update matches that have not started yet',
                    )
                  }
                },
              },
            ],
          })}
          expandedRowRender={(item) => {
            const gameSettings = item?.infos?.gameSettings
            return (
              <div>
                {/* Static Game Settings Section - Shows only once per expanded row */}
                {gameSettings && (
                  <GameSettingsContainer>
                    <GameSettingCard>
                      <GameSettingTitle>First Place</GameSettingTitle>
                      <GameSettingValue>
                        <div>
                          <span className="label">Number:</span>
                          <span className="value">
                            {gameSettings.firstPlace?.number}
                          </span>
                        </div>
                        <div>
                          <span className="label">Price:</span>
                          <span className="value">
                            Rs {gameSettings.firstPlace?.price}
                          </span>
                        </div>
                      </GameSettingValue>
                    </GameSettingCard>

                    <GameSettingCard>
                      <GameSettingTitle>Per Kill</GameSettingTitle>
                      <GameSettingValue>
                        <div>
                          <span className="label">Number:</span>
                          <span className="value">
                            {gameSettings.perKill?.number}
                          </span>
                        </div>
                        <div>
                          <span className="label">Price:</span>
                          <span className="value">
                            Rs {gameSettings.perKill?.price}
                          </span>
                        </div>
                      </GameSettingValue>
                    </GameSettingCard>

                    <GameSettingCard>
                      <GameSettingTitle>Top Rank</GameSettingTitle>
                      <GameSettingValue>
                        <div>
                          <span className="label">Number:</span>
                          <span className="value">
                            {gameSettings.topRank?.number}
                          </span>
                        </div>
                        <div>
                          <span className="label">Price:</span>
                          <span className="value">
                            Rs {gameSettings.topRank?.price}
                          </span>
                        </div>
                      </GameSettingValue>
                    </GameSettingCard>
                  </GameSettingsContainer>
                )}

                <Table
                  loading={expandedLoading}
                  totalItems={joinedByUserResults?.totalItems}
                  totalPages={joinedByUserResults?.totalPages}
                  pageControlled={joinedByPage}
                  onPageChange={setJoinedByPage}
                  columns={buildChildColumns()}
                  data={
                    expandedData[item.id]?.map((user) => {
                      const id = user?.user?.id || user?.userId
                      return {
                        id,
                        player_name: user?.user?.name ?? 'Unknown',
                        phone_no: user?.user?.phoneNo ?? 'N/A',
                        gamer_tag: user?.infos ?? 'N/A',
                        // gamer_tag: user?.user?.infos ?? 'N/A',
                        claimedStatus: user?.status ?? 'N/A',
                        points: user?.points ?? 'N/A',
                        matchJoinId: user?.id,
                        gameSettings: item?.infos?.gameSettings,
                        potentialWinning: item.potentialWinning,
                        ...(isResult && editableInputs[id]),
                      }
                    }) || []
                  }
                  hasExportButton={false}
                  hasSearchbar={false}
                />
              </div>
            )
          }}
        />
      </div>
    </Fragment>
  )
}

export default AdminCreatedMatches

const Input = styled.input`
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  width: 15%;
`

const StatusDropdown = styled.select`
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  background-color: white;
  color: ${theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding-right: 30px;
  min-width: 180px;
  transform: translateY(-65px);
  trn &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  &:hover {
    border-color: ${theme.colors.primary};
  }
`

const FilterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  padding: 0 16px;
`

export const StatusAlternate = styled.p`
  padding: 8px 12px;
  font-size: 13px;
  text-transform: capitalize;
  cursor: pointer;
  color: ${theme.colors.primary};
  background-color: ${theme.colors.primaryLight};
  width: fit-content;
  border-radius: 5px;
`

const GameSettingsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: ${({ theme }: any) => theme.colors.brown50};
  border-radius: 6px;
  border: 1px solid ${({ theme }: any) => theme.colors.borderColor};
`

const GameSettingCard = styled.div`
  flex: 1;
  padding: 8px 12px;
  background: ${({ theme }: any) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${({ theme }: any) => theme.colors.borderColor};
`

const GameSettingTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }: any) => theme.colors.textGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const GameSettingValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  div {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .label {
    font-size: 12px;
    color: ${({ theme }: any) => theme.colors.text50};
    font-weight: 500;
  }

  .value {
    font-size: 13px;
    color: ${({ theme }: any) => theme.colors.text};
    font-weight: 600;
  }
`
